import { useEffect, useState } from "react";
import { Alert, Box, Button, IconButton, Typography } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { sessionAtom } from "../../features/accounts/account.state";
import { STRIPE_GATEWAY } from "../../features/accounts/account";
import {
  clickYearlyOffer,
  dismissYearlyOffer,
  loadYearlyOffer,
} from "../../features/billing/yearlyOffer";

// Approved by Andriy 2026-10-14 (option 1 of the drafts). The second sentence
// is what Stripe does on the switch (verified 10-14: unused month credited, one invoice). "40%" is
// true only because coupon payers are excluded on the server. No refund, credit or limit lift offered.
const HEADLINE =
  "Switch to yearly billing and save 40%. The unused part of this month is credited.";
const CTA = "See yearly price";

/** Not rendered where the user is mid-task, or on the plan page the CTA opens. */
const HIDDEN_ON = ["/concierge", "/account/billing/plan"];

/**
 * S98 — the one-time yearly-plan line for active monthly payers in months 2-12. One dismissable
 * `Alert` (the `LowCreditBanner` shape), not a modal. The server decides who sees it and spends the
 * showing; see `features/billing/yearlyOffer.ts`.
 */
export const YearlyOfferLine = () => {
  const session = useAtomValue(sessionAtom);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<string | null>(null);

  const account = session?.account;
  const asks = !!account?.isPaying && account.gateway === STRIPE_GATEWAY;

  useEffect(() => {
    if (!asks) return;
    let live = true;
    loadYearlyOffer()
      .then((p) => live && setPlan(p))
      .catch(() => {
        // No line is the safe failure.
      });
    return () => {
      live = false;
    };
  }, [asks]);

  if (!plan || HIDDEN_ON.some((p) => pathname.startsWith(p))) return null;

  const handleDismiss = () => {
    setPlan(null);
    dismissYearlyOffer().catch(() => {});
  };

  const handleClick = async () => {
    setPlan(null);
    await clickYearlyOffer().catch(() => {});
    navigate(`/account/billing/plan?period=12&plan=${encodeURIComponent(plan)}`);
  };

  return (
    // The close button lives inside `action`: MUI's Alert drops its own × whenever `action` is set.
    <Alert
      severity="info"
      sx={{
        py: 0.5,
        alignItems: "center",
        borderRadius: 0,
        borderBottom: 1,
        borderColor: "divider",
      }}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button size="small" onClick={handleClick} sx={{ whiteSpace: "nowrap" }}>
            {CTA}
          </Button>
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleDismiss}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <Typography variant="body2" component="span">
        {HEADLINE}
      </Typography>
    </Alert>
  );
};
